import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FaDownload, FaFilePdf, FaImage, FaMobileAlt, FaDesktop, FaUpload, FaSpinner, FaSave } from 'react-icons/fa';
import './ExportModal.css';

const ExportModal = ({ onClose }) => {
  const [exportSettings, setExportSettings] = useState({
    format: 'pdf', // 'pdf' or 'png'
    paperSize: 'A4', // 'A3', 'A4', 'A5'
    orientation: 'portrait', // 'portrait' or 'landscape'
    quality: 1.0, // 0.5 - 2.0
    filename: 'PEA-Poster',
  });
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const paperSizes = {
    A3: { width: 297, height: 420 }, // mm
    A4: { width: 210, height: 297 },
    A5: { width: 148, height: 210 },
  };

  const handleExport = async () => {
    setExporting(true);
    setProgress(0);

    try {
      // Find the actual canvas element (not canvas-container)
      const canvasElement = document.querySelector('.canvas');
      if (!canvasElement) {
        alert('ไม่พบ Canvas ที่จะ Export');
        return;
      }

      setProgress(20);

      // Capture canvas as image with high quality
      const canvasWidth = 794;
      const canvasHeight = 1122;

      const canvas = await html2canvas(canvasElement, {
        scale: exportSettings.quality * 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: canvasWidth,
        height: canvasHeight,
        windowWidth: canvasWidth,
        windowHeight: canvasHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          const clonedCanvas = clonedDoc.querySelector('.canvas');
          if (clonedCanvas) {
            // Reset transform to ensure full capture at 100% scale
            clonedCanvas.style.transform = 'none';
            clonedCanvas.style.width = `${canvasWidth}px`;
            clonedCanvas.style.height = `${canvasHeight}px`;
            clonedCanvas.style.position = 'absolute';
            clonedCanvas.style.top = '0';
            clonedCanvas.style.left = '0';
            clonedCanvas.style.margin = '0';
            clonedCanvas.style.boxShadow = 'none'; // Remove shadow for export
            clonedCanvas.style.overflow = 'visible'; // Force visible overflow
            
            // Fix for sections being cut off
            const sections = clonedCanvas.querySelectorAll('.section');
            sections.forEach(section => {
              section.style.overflow = 'visible';
            });

            // Fix for detail text container scrolling
            const detailContainers = clonedCanvas.querySelectorAll('.detail-text-container');
            detailContainers.forEach(container => {
              container.style.overflow = 'visible';
              container.style.height = 'auto';
            });

            // Also ensure the wrapper doesn't clip it
            const wrapper = clonedDoc.querySelector('.canvas-wrapper');
            if (wrapper) {
              wrapper.style.overflow = 'visible';
              wrapper.style.height = 'auto';
              wrapper.style.width = 'auto';
              wrapper.style.padding = '0';
              wrapper.style.display = 'block';
            }
          }
        }
      });

      setProgress(60);

      if (exportSettings.format === 'png') {
        // Export as PNG (Full Canvas)
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${exportSettings.filename}.png`;
          link.click();
          URL.revokeObjectURL(url);
          setProgress(100);
          setTimeout(() => {
            setExporting(false);
            onClose();
          }, 500);
        }, 'image/png');
      } else {
        // Export as PDF
        const imgData = canvas.toDataURL('image/png');
        const size = paperSizes[exportSettings.paperSize];
        const orientation = exportSettings.orientation;
        const pdfWidth = orientation === 'portrait' ? size.width : size.height;
        const pdfHeight = orientation === 'portrait' ? size.height : size.width;
        
        const pdf = new jsPDF({
          orientation: orientation,
          unit: 'mm',
          format: exportSettings.paperSize,
        });

        // Fit image to PDF page (Contain)
        const imgProps = pdf.getImageProperties(imgData);
        const pdfRatio = pdfWidth / pdfHeight;
        const imgRatio = imgProps.width / imgProps.height;
        
        let finalWidth = pdfWidth;
        let finalHeight = pdfHeight;
        
        if (imgRatio > pdfRatio) {
          // Image is wider than page
          finalHeight = pdfWidth / imgRatio;
        } else {
          // Image is taller than page
          finalWidth = pdfHeight * imgRatio;
        }
        
        const x = (pdfWidth - finalWidth) / 2;
        const y = (pdfHeight - finalHeight) / 2;

        pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
        pdf.save(`${exportSettings.filename}.pdf`);
        
        setProgress(100);
        setTimeout(() => {
          setExporting(false);
          onClose();
        }, 500);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('เกิดข้อผิดพลาดในการ Export: ' + error.message);
      setExporting(false);
    }
  };

  const handleChange = (key, value) => {
    setExportSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3><FaDownload /> Export โปสเตอร์</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="export-modal-content">
          {!exporting ? (
            <>
              <div className="export-group">
                <label>รูปแบบไฟล์</label>
                <div className="format-buttons">
                  <button
                    className={`format-btn ${exportSettings.format === 'pdf' ? 'active' : ''}`}
                    onClick={() => handleChange('format', 'pdf')}
                  >
                    <FaFilePdf /> PDF
                  </button>
                  <button
                    className={`format-btn ${exportSettings.format === 'png' ? 'active' : ''}`}
                    onClick={() => handleChange('format', 'png')}
                  >
                    <FaImage /> PNG Image
                  </button>
                </div>
              </div>

              <div className="export-group">
                <label>ขนาดกระดาษ</label>
                <div className="paper-size-buttons">
                  {Object.keys(paperSizes).map(size => (
                    <button
                      key={size}
                      className={`paper-btn ${exportSettings.paperSize === size ? 'active' : ''}`}
                      onClick={() => handleChange('paperSize', size)}
                    >
                      {size}
                      <span className="size-info">
                        {paperSizes[size].width}×{paperSizes[size].height}mm
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="export-group">
                <label>การวางแนว</label>
                <div className="orientation-buttons">
                  <button
                    className={`orientation-btn ${exportSettings.orientation === 'portrait' ? 'active' : ''}`}
                    onClick={() => handleChange('orientation', 'portrait')}
                  >
                    <FaMobileAlt /> แนวตั้ง
                  </button>
                  <button
                    className={`orientation-btn ${exportSettings.orientation === 'landscape' ? 'active' : ''}`}
                    onClick={() => handleChange('orientation', 'landscape')}
                  >
                    <FaDesktop /> แนวนอน
                  </button>
                </div>
              </div>

              <div className="export-group">
                <label>คุณภาพ: {exportSettings.quality}x</label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={exportSettings.quality}
                  onChange={(e) => handleChange('quality', parseFloat(e.target.value))}
                  className="quality-slider"
                />
                <div className="slider-labels">
                  <span>เร็ว (0.5x)</span>
                  <span>ปกติ (1x)</span>
                  <span>คมชัด (3x)</span>
                </div>
              </div>

              <div className="export-group">
                <label>ชื่อไฟล์</label>
                <input
                  type="text"
                  value={exportSettings.filename}
                  onChange={(e) => handleChange('filename', e.target.value)}
                  placeholder="ชื่อไฟล์"
                  className="filename-input"
                />
              </div>
            </>
          ) : (
            <div className="export-progress">
              <div className="progress-icon"><FaUpload /></div>
              <h4>กำลัง Export...</h4>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <p>{progress}%</p>
            </div>
          )}
        </div>

        <div className="export-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={exporting}>
            ยกเลิก
          </button>
          <button className="btn-export" onClick={handleExport} disabled={exporting}>
            {exporting ? <><FaSpinner className="fa-spin" /> กำลังประมวลผล...</> : <><FaSave /> Export</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
